import {ariaLabelDate} from '@github-ui/aria-label-date'
import type {LineRange} from '@github-ui/diffs/types'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import safeStorage from '@github-ui/safe-storage'
import {useAnalytics} from '@github-ui/use-analytics'
import {ChevronDownIcon, ChevronRightIcon, StopIcon} from '@primer/octicons-react'
import {Button, Flash, IconButton, Label, Spinner} from '@primer/react'
import {clsx} from 'clsx'
import type {PropsWithChildren} from 'react'
import {Suspense, useEffect, useMemo, useRef, useState} from 'react'

import {CommentErrorFallback} from './CommentErrorFallback'
import {useInlineCommentDialogModeInteractiveElements} from '../../hooks/use-inline-comment-dialog-mode-interactive-elements'
import type {
  CommentAuthor,
  ConfigureSuggestedChangesImplementation,
  Thread,
  ThreadPositioning,
} from '../../conversation-types'
import {AutomatedCommentSource, CommentReviewVariantType} from '../../conversation-types'
import styles from './InlineReviewThread.module.css'
import type {ReviewThreadProps} from './ReviewThread'
import {ReviewThread} from './ReviewThread'
import {useDialogModeActions, useDialogModeIsEnabled} from '../../stores/selectors'
import {useThread} from '@github-ui/pull-requests/pull-request-store-selectors'
import {useResolveThreadMutation} from '@github-ui/pull-requests/use-resolve-thread-mutation'
import {useUnresolveThreadMutation} from '@github-ui/pull-requests/use-unresolve-thread-mutation'
import type {LineKey} from '../../types'
import {InlineAutomatedReviewThreadActions} from './InlineAutomatedReviewThreadActions'
import {isLoggedIn} from '@github-ui/client-env'

function Emphasis({children}: PropsWithChildren) {
  return <span className="text-semibold fgColor-default">{children}</span>
}

function ThreadBanner({thread}: {thread?: Thread}) {
  if (typeof thread === 'undefined') return null

  const positioning = thread.positioning ?? thread.subject // support both for backwards compatability

  if (typeof positioning === 'undefined') return null

  if (!positioning.endDiffSide || !positioning.endLine) return null

  if (!positioning.startLine || !positioning.startDiffSide || positioning.startLine === positioning.endLine) {
    return SingleLineThreadBanner({positioning})
  } else {
    return MultineLineThreadBanner({positioning})
  }
}

function SingleLineThreadBanner({positioning}: {positioning: Omit<ThreadPositioning, 'isOutdated'>}) {
  const diffSideLetter = positioning.endDiffSide === 'LEFT' ? 'L' : 'R'
  return (
    <h2 className={styles.inlineReviewThreadHeading}>
      Comment on line{' '}
      <Emphasis>
        {diffSideLetter}
        {positioning.endLine ?? ''}
      </Emphasis>
    </h2>
  )
}

function MultineLineThreadBanner({positioning}: {positioning: Omit<ThreadPositioning, 'isOutdated'>}) {
  const startSideString = positioning.startDiffSide === 'LEFT' ? 'L' : 'R'
  const endSideString = positioning.endDiffSide === 'LEFT' ? 'L' : 'R'
  return (
    <h2 className={styles.inlineReviewThreadHeading}>
      Comment on lines{' '}
      <Emphasis>
        {startSideString}
        {positioning.startLine ?? ''}
      </Emphasis>{' '}
      to{' '}
      <Emphasis>
        {endSideString}
        {positioning.endLine ?? ''}
      </Emphasis>
    </h2>
  )
}

export interface InlineReviewThreadProps
  extends Pick<ReviewThreadProps, 'batchingEnabled' | 'subjectId' | 'viewerData'> {
  threadId: string
  fileAnchor?: string
  filePath: string
  lineMarkersKey: LineKey
  gridCellId: string
  onDeleteLineComment: () => void
  threadsConnectionId?: string
  ghostUser?: CommentAuthor
  manuallyUpdateCommentsWithThisThreadId?: string
  isFirstThread?: boolean
  threadPositionNumber?: number
  suggestedChangesConfig?: ConfigureSuggestedChangesImplementation
}

/**
 * The InlineReviewThread opens from the ActionBar when the user selects a conversation.
 */
export function InlineReviewThread({
  fileAnchor,
  filePath,
  gridCellId,
  onDeleteLineComment,
  threadId,
  threadPositionNumber,
  lineMarkersKey,
  threadsConnectionId,
  isFirstThread,
  ghostUser,
  manuallyUpdateCommentsWithThisThreadId,
  suggestedChangesConfig,
  ...rest
}: InlineReviewThreadProps) {
  const thread = useThread(threadId, filePath, lineMarkersKey)

  const {mutate: resolveThreadMutation} = useResolveThreadMutation(filePath, lineMarkersKey)
  const handleResolveThread = () => {
    if (!thread) return

    resolveThreadMutation(
      {threadId: thread.id},
      {
        onError: () => setErrorMessage('Failed to resolve thread'),
        onSuccess: () => {
          setIsCollapsed(true)
          safeLocalStorage.removeItem(`reviewThreadIsCollapsed_${threadId}`)
        },
      },
    )

    sendAnalyticsEvent('comments.resolve_thread', 'RESOLVE_CONVERSATION_BUTTON')
  }

  const reviewThreadRef = useRef<HTMLDivElement>(null)
  const safeLocalStorage = safeStorage('localStorage')
  const isResolved = thread?.isResolved
  const [errorMessage, setErrorMessage] = useState('')
  const [isCollapsed, setIsCollapsed] = useState<boolean>(isResolved ?? false)
  const isInDialogMode = useDialogModeIsEnabled(gridCellId)
  const {disableDialogMode, enableDialogMode} = useDialogModeActions()

  useEffect(() => {
    const storedState = localStorage.getItem(`reviewThreadIsCollapsed_${threadId}`)
    if (storedState !== null) {
      setIsCollapsed(JSON.parse(storedState))
    } else if (isResolved) {
      setIsCollapsed(true)
    }
  }, [isResolved, threadId])

  const handleToggleCollapsed = () => {
    window.requestAnimationFrame(() => {
      safeLocalStorage.setItem(`reviewThreadIsCollapsed_${threadId}`, JSON.stringify(!isCollapsed))
    })
    setIsCollapsed((prevIsCollapsed: boolean) => !prevIsCollapsed)
  }

  useInlineCommentDialogModeInteractiveElements({
    commentSubjectType: 'pull request',
    gridCellId,
    markerRef: reviewThreadRef,
    isThread: true,
    thread,
  })

  const config = useMemo(() => {
    const positioning = thread?.positioning ?? thread?.subject // support both for backwards compatibility in the interim

    if (!positioning) {
      return undefined
    }

    if (
      fileAnchor &&
      thread &&
      positioning.startDiffSide &&
      positioning.endDiffSide &&
      positioning.startLine &&
      positioning.endLine &&
      suggestedChangesConfig?.configureSuggestedChangesFromLineRange
    ) {
      const startLine = positioning.startLine ?? positioning.endLine
      const startDiffSide = positioning.startDiffSide ?? positioning.endDiffSide

      const threadRowRange: LineRange = {
        diffAnchor: fileAnchor,
        endLineNumber: positioning.endLine,
        endOrientation: positioning.endDiffSide === 'LEFT' ? 'left' : 'right',
        startLineNumber: startLine,
        startOrientation: startDiffSide === 'LEFT' ? 'left' : 'right',
        firstSelectedLineNumber: startLine,
        firstSelectedOrientation: startDiffSide === 'LEFT' ? 'left' : 'right',
      }
      return suggestedChangesConfig?.configureSuggestedChangesFromLineRange(threadRowRange)
    }

    return undefined
  }, [fileAnchor, thread, suggestedChangesConfig])

  const {sendAnalyticsEvent} = useAnalytics()

  const {mutate: unresolveThreadMutation} = useUnresolveThreadMutation(filePath, lineMarkersKey)
  const handleUnresolveThread = () => {
    if (!thread) return

    unresolveThreadMutation(
      {threadId: thread.id},
      {
        onError: () => setErrorMessage('Failed to unresolve thread'),
        onSuccess: () => {
          setIsCollapsed(false)
          safeLocalStorage.removeItem(`reviewThreadIsCollapsed_${thread.id}`)
        },
      },
    )

    sendAnalyticsEvent('comments.unresolve_thread', 'RESOLVE_CONVERSATION_BUTTON')
  }

  const isAutomatedReviewThread =
    thread?.commentsData?.comments[0]?.reviewVariantType === CommentReviewVariantType.Automated
  const automatedComment = isAutomatedReviewThread ? thread.commentsData?.comments[0]?.automatedComment : null
  const dismissableAutomatedComment = automatedComment && automatedComment.dismissable
  const licenseComplianceAutomatedComment =
    automatedComment && automatedComment.source === AutomatedCommentSource.LicenseCompliance

  if (!thread) return null

  const hasComments = (thread.commentsData?.comments?.length ?? 0) > 0

  if (!hasComments) return null

  const isThreadResolved = !!thread.isResolved
  const userCanResolve = isLoggedIn()

  const isResolveable =
    userCanResolve && thread.commentsData?.comments?.some(comment => comment.state?.toUpperCase() !== 'PENDING')

  const createdAt = thread.commentsData?.comments?.[0]?.createdAt
  const commentAuthorLogin = thread.commentsData?.comments?.[0]?.author?.login ?? 'Unknown Author'

  let resolveButtonLabel = `${isResolved ? 'Unresolve' : 'Resolve'} comment`
  if (createdAt) {
    const createdAtFormatted = ariaLabelDate(new Date(createdAt))
    resolveButtonLabel = `${
      isResolved ? 'Unresolve' : 'Resolve'
    } ${commentAuthorLogin}'s comment, ${createdAtFormatted}`
  }

  return (
    <ErrorBoundary fallback={<CommentErrorFallback />}>
      <div className="rounded-2 bgColor-default" ref={reviewThreadRef}>
        <div className={clsx('px-1', styles.ReviewThreadContainer)}>
          <IconButton
            aria-label={isCollapsed ? 'Expand comment' : 'Collapse comment'}
            icon={isCollapsed ? ChevronRightIcon : ChevronDownIcon}
            size="small"
            variant="invisible"
            onClick={handleToggleCollapsed}
            data-is-first-collapse-button={isFirstThread}
          />
          <ThreadBanner thread={thread} />
          <div className={styles.ResolvableContainer}>
            {thread.positioning?.isOutdated && <Label variant="secondary">Outdated</Label>}

            {isThreadResolved && !isAutomatedReviewThread && <Label variant="secondary">Resolved</Label>}
            {automatedComment?.isDismissed && <Label variant="secondary">Dismissed</Label>}
          </div>
        </div>
        <Suspense
          fallback={
            <div className={styles.ThreadLoading}>
              <Spinner />
              <p>Loading comments</p>
            </div>
          }
        >
          {errorMessage && (
            <Flash variant="danger" className="m-2">
              <StopIcon className="mr-2" />
              {errorMessage}
            </Flash>
          )}
          {!isCollapsed && (
            <>
              <ReviewThread
                lineMarkersKey={lineMarkersKey}
                disableDialogMode={disableDialogMode}
                enableDialogMode={enableDialogMode}
                filePath={filePath}
                gridCellId={gridCellId}
                onDeleteLineComment={onDeleteLineComment}
                isInlineComment
                isInDialogMode={isInDialogMode}
                thread={thread}
                threadPositionNumber={threadPositionNumber}
                threadsConnectionId={threadsConnectionId}
                suggestedChangesConfig={config}
                shouldLimitHeight={false}
                {...rest}
                ghostUser={ghostUser}
              />
              {isResolveable && !dismissableAutomatedComment && !licenseComplianceAutomatedComment && (
                <div className="d-flex flex-items-center p-2 border-top flex-wrap">
                  <Button
                    aria-label={resolveButtonLabel}
                    onClick={isThreadResolved ? handleUnresolveThread : handleResolveThread}
                    className="mr-2"
                  >
                    {isResolved ? 'Unresolve comment' : 'Resolve comment'}
                  </Button>
                  {isThreadResolved && thread.resolvedBy && (
                    <span className="fgColor-muted f6">
                      <b>{thread.resolvedBy}</b> marked this comment as resolved
                    </span>
                  )}
                </div>
              )}
              {dismissableAutomatedComment && (
                <InlineAutomatedReviewThreadActions
                  automatedComment={automatedComment}
                  thread={thread}
                  filePath={filePath}
                  lineMarkersKey={lineMarkersKey}
                  setErrorMessage={setErrorMessage}
                  setIsCollapsed={setIsCollapsed}
                  safeLocalStorage={safeLocalStorage}
                />
              )}
            </>
          )}
        </Suspense>
      </div>
    </ErrorBoundary>
  )
}

try{ Emphasis.displayName ||= 'Emphasis' } catch {}
try{ ThreadBanner.displayName ||= 'ThreadBanner' } catch {}
try{ SingleLineThreadBanner.displayName ||= 'SingleLineThreadBanner' } catch {}
try{ MultineLineThreadBanner.displayName ||= 'MultineLineThreadBanner' } catch {}
try{ InlineReviewThread.displayName ||= 'InlineReviewThread' } catch {}